import { t } from '@hefe/i18n';
import {
  BasketballGame,
  EventReport,
  EventReportEventableTypeEnum,
  EventReportGroupableType,
  EventReportGroupableTypeEnum,
} from '@hefe/models';
import { EventReportsApi, ModalsSlice } from '@hefe/store';
import { useMemo } from 'react';
import { useAppDispatch } from '../../../../store/store';
import CustomForm from '../../../core/components/form/custom-form';

interface AddEditEventReportFormProps {
  game: BasketballGame;
  report?: EventReport;
  eventType: EventReportEventableTypeEnum;
  eventId: number;
  groupType?: EventReportGroupableTypeEnum;
  groupId: number;
}

export default function AddEditEventReportForm(
  props: AddEditEventReportFormProps
) {
  const { report } = props;

  const dispatch = useAppDispatch();

  const [createReport] = EventReportsApi.useCreateReportMutation();

  const [updateReport] = EventReportsApi.useUpdateReportMutation();

  const onSubmit = (values: any, resetForm: any) => {
    const close = ModalsSlice.modalsActions.closeModal;
    const setLoading = ModalsSlice.modalsActions.setModalLoading;

    const style = values.style;
    const groupable_type =
      style === 'neutral'
        ? EventReportGroupableType.CLUB
        : EventReportGroupableType.BASKETBALL_TEAM;

    let groupable_id = props.groupId;
    if (style.startsWith('team')) {
      const teamId = parseInt(style.split('_')[1]);
      groupable_id = teamId;
    }

    dispatch(setLoading(true));

    if (report) {
      updateReport({
        id: report.id,
        eventable_id: props.eventId,
        eventable_type: props.eventType,
        groupable_id: groupable_id,
        groupable_type: groupable_type,
        text: values.text,
        title: values.title,
        type: values.type,
        visibility: values.is_public ? 'public' : 'private',
      })
        .unwrap()
        .then(() => {
          resetForm();
          dispatch(close());
        })
        .finally(() => {
          dispatch(setLoading(false));
        });
    } else {
      createReport({
        eventable_id: props.eventId,
        eventable_type: props.eventType,
        groupable_id: groupable_id,
        groupable_type: groupable_type,
        text: values.text,
        title: values.title,
        type: values.type,
        visibility: values.is_public ? 'public' : 'private',
      })
        .unwrap()
        .then(() => {
          resetForm();
          dispatch(close());
        })
        .finally(() => {
          dispatch(setLoading(false));
        });
    }
  };

  const writingTypeOptions = useMemo(() => {
    const options = [
      {
        value: 'neutral',
        label: t('game_reports.manual.form.writing_type.neutral'),
      },
    ];

    if (props.game?.team_details) {
      for (const team of props.game.team_details) {
        options.push({
          value: 'team_' + (team?.team?.id ?? 0),
          label: team.team?.name ?? 'Mannschaft ' + team.id,
        });
      }
    }

    return options;
  }, [props.game]);

  return (
    <CustomForm
      key={`add-edit-event-report-form-${report?.id ?? 'new'}`}
      formId={`add-edit-event-report-form-${report?.id ?? 'new'}`}
      fields={[
        {
          name: 'type',
          type: 'select',
          label: t('game_reports.manual.form.type.title'),
          initalValue: report?.type,
          selectOptions: [
            { value: 'pre', label: t('game_reports.manual.form.type.pre') },
            { value: 'post', label: t('game_reports.manual.form.type.post') },
          ],
          rules: [
            {
              required: true,
              message: t('form_errors.value'),
            },
          ],
        },
        {
          name: 'style',
          type: 'select',
          label: t('game_reports.manual.form.writing_type.title'),
          extraLabel: t('game_reports.manual.form.writing_type.description'),
          initalValue:
            props.groupType === EventReportGroupableType.CLUB
              ? 'neutral'
              : 'team_' + props.groupId,
          selectOptions: writingTypeOptions,
          rules: [
            {
              required: true,
              message: t('form_errors.value'),
            },
          ],
        },
        {
          name: 'title',
          label: t('game_reports.manual.form.title'),
          type: 'text',
          initalValue: report?.title,
          rules: [
            {
              required: true,
              message: t('form_errors.name'),
            },
          ],
        },
        {
          name: 'text',
          label: t('game_reports.manual.form.text'),
          type: 'texteditor',
          initalValue: report?.text,
          rules: [
            {
              required: true,
              message: t('form_errors.text'),
            },
          ],
        },
        {
          name: 'is_public',
          label: t('game_reports.manual.form.public.title'),
          extraLabel: t('game_reports.manual.form.public.description'),
          type: 'checkbox',
          initalValue: report?.visibility === 'public',
          rules: [],
        },
      ]}
      onSubmit={onSubmit}
      loading={false}
    />
  );
}
